import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Radio } from "lucide-react";

const NAV = [
  { to: "/", label: "Accueil", testid: "nav-home" },
  { to: "/podcasts", label: "Podcasts", testid: "nav-podcasts" },
  { to: "/dimanche", label: "Méditation du dimanche", testid: "nav-sunday" },
  { to: "/a-propos", label: "À propos", testid: "nav-about" },
  { to: "/ministere", label: "Ministère", testid: "nav-ministry" },
  { to: "/temoignages", label: "Témoignages", testid: "nav-testimonies" },
  { to: "/ressources", label: "Nourris ta foi", testid: "nav-resources" },
  { to: "/faq", label: "FAQ", testid: "nav-faq" },
];

export default function Navigation() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const isPodcastsPage = pathname.startsWith("/podcasts");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const darkMode = isPodcastsPage;
  const navBg = scrolled
    ? darkMode
      ? "backdrop-blur-md bg-[#0A0806]/85 border-b border-white/10"
      : "backdrop-blur-md bg-[#F7F5F0]/85 border-b border-[#DDE3E8]"
    : "bg-transparent";

  const linkClass = ({ isActive }) => {
    const base = "px-3 py-2 rounded-full text-[13px] transition-all tracking-wide";
    if (darkMode) {
      return `${base} ${isActive ? "text-white bg-white/10" : "text-white/70 hover:text-white hover:bg-white/5"}`;
    }
    return `${base} ${isActive ? "text-[#1E2933] bg-[#E4EBEF]" : "text-[#1E2933]/70 hover:text-[#1E2933] hover:bg-[#E4EBEF]/70"}`;
  };

  const logoColor = darkMode ? "text-[#E63946]" : "text-[#4A6D82]";
  const ctaColor = darkMode
    ? "bg-[#E63946] hover:bg-[#C1121F] text-white jt-red-glow"
    : "bg-[#4A6D82] hover:bg-[#2E4959] text-white jt-blue-glow";

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[70px] flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group shrink-0">
          <img src="/assets/logo.jpg" alt="Jesus Talk" className="h-10 w-10 rounded-full object-cover ring-1 ring-white/20" />
          <span className={`font-display text-[22px] leading-none tracking-widest ${logoColor}`}>
            JESUS TALK
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-0.5">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={n.testid} end={n.to === "/"} className={linkClass}>
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center shrink-0">
          <Link
            to="/podcasts"
            data-testid="nav-cta"
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm transition-colors ${ctaColor}`}
          >
            <Radio className="w-4 h-4" strokeWidth={1.75} />
            Écouter
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className={`lg:hidden p-2 rounded-full ${darkMode ? "text-white hover:bg-white/10" : "text-[#1E2933] hover:bg-[#E4EBEF]"}`}
          aria-label="Menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className={`lg:hidden border-b px-5 py-4 flex flex-col gap-1 ${darkMode ? "bg-[#0A0806] border-white/10" : "bg-[#F7F5F0] border-[#DDE3E8]"}`}>
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`${n.testid}-mobile`}
              end={n.to === "/"}
              className={({ isActive }) =>
                `px-4 py-3 rounded-xl text-base ${
                  darkMode
                    ? isActive ? "bg-white/10 text-white" : "text-white/80"
                    : isActive ? "bg-[#E4EBEF] text-[#1E2933]" : "text-[#1E2933]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </div>
      )}
    </header>
  );
}
